import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { userFormSchema, type UserFormData } from '@geely/shared/schemas';
import { Eye, EyeOff, Check, ChevronDown, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Badge,
  Checkbox,
} from "@geely/ui";
import { cn } from '@/lib/utils';
import type { UserWithoutPassword, UserRole, Store } from '@geely/shared';

interface UserFormDialogProps {
  isOpen: boolean;
  editingUser: UserWithoutPassword | null;
  stores: Store[];
  onSubmit: (data: UserFormData) => Promise<void>;
  onClose: () => void;
}

export function UserFormDialog({
  isOpen,
  editingUser,
  stores,
  onSubmit,
  onClose,
}: UserFormDialogProps) {
  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'store_admin' as UserRole,
      storeIds: [],
      _isEditing: false,
    },
    mode: 'onBlur',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [storePopoverOpen, setStorePopoverOpen] = useState(false);

  const { formState: { isSubmitting } } = form;
  const watchedRole = form.watch('role');

  useEffect(() => {
    if (isOpen) {
      setShowPassword(false);
      setShowConfirmPassword(false);
      setStorePopoverOpen(false);
      if (editingUser) {
        form.reset({
          name: editingUser.name,
          email: editingUser.email,
          password: '',
          confirmPassword: '',
          role: editingUser.role as UserRole,
          storeIds: editingUser.storeIds ?? (editingUser.storeId ? [editingUser.storeId] : []),
          _isEditing: true,
        });
      } else {
        form.reset({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'store_admin' as UserRole,
          storeIds: [],
          _isEditing: false,
        });
      }
    }
  }, [isOpen, editingUser, form]);

  useEffect(() => {
    if (watchedRole === 'admin') {
      form.setValue('storeIds', []);
    }
  }, [watchedRole, form]);

  const toggleStore = (storeId: string, currentIds: string[]) => {
    if (currentIds.includes(storeId)) {
      return currentIds.filter((id) => id !== storeId);
    }
    return [...currentIds, storeId];
  };

  const getStoreName = (storeId: string) => {
    return stores.find((s) => s.id === storeId)?.name ?? storeId;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
          </DialogTitle>
          <DialogDescription>
            Preencha os dados do usuário
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Senha {editingUser ? '' : '*'}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          {...field}
                          placeholder={editingUser ? 'Deixe em branco para manter a senha atual' : ''}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmar Senha {editingUser ? '' : '*'}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...field}
                          placeholder={editingUser ? 'Confirme a nova senha' : ''}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nível de Permissão *</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value: UserRole) => field.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o nível" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="admin">Admin Geral</SelectItem>
                        <SelectItem value="store_admin">Admin de Loja</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchedRole === 'store_admin' && (
                <FormField
                  control={form.control}
                  name="storeIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lojas *</FormLabel>
                      <Popover modal={false} open={storePopoverOpen} onOpenChange={setStorePopoverOpen}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            role="combobox"
                            aria-expanded={storePopoverOpen}
                            className={cn(
                              'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-11 w-full items-center justify-between gap-2 rounded-md border bg-input-background px-4 py-2.5 text-base whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
                              field.value.length === 0 && 'text-muted-foreground'
                            )}
                          >
                            <span className="line-clamp-1">
                              {field.value.length === 0
                                ? 'Selecione as lojas'
                                : `${field.value.length} loja${field.value.length > 1 ? 's' : ''} selecionada${field.value.length > 1 ? 's' : ''}`}
                            </span>
                            <ChevronDown className="size-4 shrink-0 opacity-50" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Buscar loja..." />
                            <CommandList className="max-h-[164px]" onWheel={(e) => e.stopPropagation()}>
                              <CommandEmpty>Nenhuma loja encontrada.</CommandEmpty>
                              <CommandGroup>
                                {stores.map((store) => {
                                  const isSelected = field.value.includes(store.id);
                                  return (
                                    <CommandItem
                                      key={store.id}
                                      value={store.name}
                                      onSelect={() => {
                                        const newIds = toggleStore(store.id, field.value);
                                        field.onChange(newIds);
                                      }}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        className="mr-2"
                                      />
                                      {store.name}
                                      <Check
                                        className={cn(
                                          'ml-auto h-4 w-4',
                                          isSelected ? 'opacity-100' : 'opacity-0'
                                        )}
                                      />
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {field.value.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {field.value.map((storeId) => (
                            <Badge
                              key={storeId}
                              variant="secondary"
                              className="text-xs"
                            >
                              {getStoreName(storeId)}
                              <button
                                type="button"
                                className="ml-1 rounded-full outline-none hover:bg-secondary"
                                onClick={() => {
                                  const newIds = field.value.filter((id) => id !== storeId);
                                  field.onChange(newIds);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}

                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Carregando...' : (editingUser ? 'Atualizar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
